import { inject, Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { UserService } from './user.service';
import {
  buildAuditUserNamesLookupFromUsers,
  collectBareAuditUserIds,
} from '../utils/company-audit-display.util';

/**
 * Shared uid → display name map for audit popovers: bulk users API, optional merge from a loaded list,
 * and per-UID lookup via getById. Safe to inject across list pages; map accumulates for the session.
 */
@Injectable({ providedIn: 'root' })
export class AuditUserDirectoryService {
  private readonly authService = inject(AuthService);
  private readonly userService = inject(UserService);
  private map: Record<string, string> = {};

  /** Latest merged directory (object reference updates when the map changes). */
  get directory(): Readonly<Record<string, string>> {
    return this.map;
  }

  async prefetchFromUserApi(limit = 500): Promise<void> {
    if (!this.authService.canBulkListAllUsers()) {
      return;
    }
    try {
      const { items } = await this.userService.getAll({ limit });
      this.map = { ...this.map, ...buildAuditUserNamesLookupFromUsers(items) };
    } catch {
      /* keep existing map */
    }
  }

  mergeFromLoadedUsers(
    users: Parameters<typeof buildAuditUserNamesLookupFromUsers>[0]
  ): void {
    this.map = { ...this.map, ...buildAuditUserNamesLookupFromUsers(users) };
  }

  clear(): void {
    this.map = {};
  }

  /**
   * Returns a map that includes all known names plus any resolved via getById for this row.
   * Updates the shared directory with newly fetched names.
   */
  async resolveForRow(row: unknown): Promise<Record<string, string>> {
    const merged: Record<string, string> = { ...this.map };
    const fromGetById: Record<string, string> = {};
    for (const uid of collectBareAuditUserIds(row)) {
      if (merged[uid]?.trim()) {
        continue;
      }
      try {
        const u = await this.userService.getById(uid);
        const n = u?.name?.trim();
        if (n) {
          merged[uid] = n;
          fromGetById[uid] = n;
        }
      } catch {
        /* leave unresolved */
      }
    }
    if (Object.keys(fromGetById).length > 0) {
      this.map = { ...this.map, ...fromGetById };
    }
    return merged;
  }
}
